<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- HEADER -->
    <header class="sa-hero-header mb-4">
      <div class="sa-hero-text">
        <PageBreadcrumb :items="[{ text: 'Audit Log' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="role-badge-pill">Platform Activity</span>
        </div>
        <p class="sa-subtitle">
          Track every store and platform admin created, edited, or removed by Super Admins
        </p>
      </div>

      <div class="sa-hero-actions">
        <button class="action-btn outline-btn" @click="getData()" title="Refresh List">
          <v-icon left size="16" color="#334155">mdi-refresh</v-icon>
          Refresh
        </button>
      </div>
    </header>

    <!-- NO DATA STATE -->
    <div v-if="!appLoading && logList.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#64748b" large class="mb-2">mdi-history</v-icon>
      <div class="no-data-text">No audit log entries yet.</div>
    </div>

    <!-- TABLE -->
    <v-card v-if="logList.length > 0" outlined class="rounded-lg overflow-hidden elevation-1 mb-4" color="#FFFFFF">
      <div class="table-container">
        <table class="pro-admin-table">
          <thead>
            <tr>
              <th class="text-left">DATE &amp; TIME</th>
              <th class="text-left">PERFORMED BY</th>
              <th class="text-left">ACTION</th>
              <th class="text-left">STORE</th>
              <th class="text-left">DETAILS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(value, i) in logList" :key="value._id || i" class="table-row">
              <td class="text-left contact-line">{{ formatDate(value.create_date) }}</td>
              <td class="text-left">
                <span v-if="value.userId">{{ value.userId.firstname || value.userId.username }} {{ value.userId.lastname || '' }}</span>
                <span v-else>—</span>
              </td>
              <td class="text-left">
                <span class="action-pill">{{ humanizeAction(value.action) }}</span>
              </td>
              <td class="text-left">
                <span v-if="value.jewelleryId">{{ value.jewelleryId.jewelleryName || value.jewelleryId.username }}</span>
                <span v-else>Platform</span>
              </td>
              <td class="text-left">
                <span v-if="value.note">{{ formatNote(value.note) }}</span>
                <span v-else-if="value.changes && Object.keys(value.changes).length">
                  <span v-for="(change, field) in value.changes" :key="field">
                    {{ formatFieldName(field) }} changed from {{ formatFieldValue(field, change.old) }} to {{ formatFieldValue(field, change.new) }}<br />
                  </span>
                </span>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINATION -->
      <div class="pa-4 d-flex align-center justify-space-between flex-wrap" style="border-top: 1px solid #E2E8F0;" v-if="Pagelength > 1">
        <span class="text-caption text-muted font-weight-medium">
          Showing Page {{ page }} of {{ Pagelength }} ({{ totalCount }} Total Entries)
        </span>
        <v-pagination
          small
          color="#0F172A"
          v-model="page"
          :length="Pagelength"
          total-visible="7"
          @input="getData"
        ></v-pagination>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SuperAdminAuditLog",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      logList: [],
      page: 1,
      Pagelength: 1,
      totalCount: 0,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/auditlog/superadmin/list",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          limit: 25,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.logList = response.data.data || [];
            this.totalCount = response.data.totalCount || 0;
            this.Pagelength = Math.ceil(this.totalCount / 25) || 1;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    formatDate(date) {
      if (!date) return "—";
      var d = new Date(date);
      return d.toLocaleString();
    },
    // "purchase.payment" -> "Purchase – Payment", "jewelleryAdmin.edit" -> "Jewellery Admin – Edit"
    humanizeAction(action) {
      if (!action) return "—";
      return action
        .split(".")
        .map((part) => this.formatFieldName(part))
        .join(" – ");
    },
    // "jewelleryName" -> "Jewellery Name", "GSTIN" -> "GSTIN"
    formatFieldName(key) {
      if (!key) return "";
      var spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " ");
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    },
    formatFieldValue(key, value) {
      if (value === undefined || value === null || value === "") return "—";
      if (/amount|rate|price/i.test(key) && !isNaN(parseFloat(value))) {
        return "₹" + Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return value;
    },
    // Splits backend-built "key=value, key=value" notes into a readable line;
    // natural-language notes (e.g. "Created platform admin ...") pass through unchanged.
    formatNote(note) {
      if (!note) return "—";
      var parts = note.split(", ");
      var pairs = [];
      for (var i = 0; i < parts.length; i++) {
        var idx = parts[i].indexOf("=");
        if (idx === -1) return note;
        pairs.push({ key: parts[i].slice(0, idx), value: parts[i].slice(idx + 1) });
      }
      return pairs
        .map((p) => this.formatFieldName(p.key) + ": " + this.formatFieldValue(p.key, p.value))
        .join(" · ");
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.sa-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.role-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.sa-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sa-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.table-container {
  overflow-x: auto;
  width: 100%;
}

.pro-admin-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.pro-admin-table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.pro-admin-table td {
  padding: 12px 16px;
  vertical-align: top;
  border-bottom: 1px solid #F1F5F9;
  color: #0F172A;
}

.table-row:hover td {
  background-color: #F8FAFC !important;
}

.contact-line {
  font-size: 12.5px;
  color: #334155;
  white-space: nowrap;
}

.action-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 700;
  background: #EFF6FF;
  color: #2563EB;
  border: 1px solid #BFDBFE;
  white-space: nowrap;
}
</style>
